import type { DynamicClient, NetworkData } from '@dynamic-labs-sdk/client';
import { getCore } from '@dynamic-labs-sdk/client/core';

import { RpcError } from '../../errors/RpcError';

type FetchEvmBalanceParams = {
  address: string;
  networkData: NetworkData;
};

/** @not-instrumented */
export const fetchEvmBalance = async (
  { address, networkData }: FetchEvmBalanceParams,
  client: DynamicClient
): Promise<string | null> => {
  const rpcUrl = networkData.rpcUrls.http[0];
  const core = getCore(client);

  try {
    // Perform a POST request to the RPC URL with the JSON-RPC payload
    const response = await core.fetch(rpcUrl, {
      /**
       * The JSON-RPC method 'eth_getBalance' takes the address and a block tag ('latest' is the most recent)
       * See EIP-1474: https://eips.ethereum.org/EIPS/eip-1474
       * See eth_getBalance spec: https://ethereum.org/en/developers/docs/apis/json-rpc/#eth_getbalance
       */
      body: JSON.stringify({
        id: Math.floor(Date.now() + Math.random() * 1000),
        jsonrpc: '2.0',
        method: 'eth_getBalance',
        params: [address, 'latest'],
      }),

      headers: {
        'Content-Type': 'application/json',
      },

      method: 'POST',
    });

    // Check if the network response was successful
    if (!response.ok) {
      throw new RpcError({
        code: 'http_error',
        shortMessage: `HTTP error! status: ${response.status}`,
      });
    }

    const data = await response.json();

    // Check for a JSON-RPC error in the response
    if (data.error) {
      throw new RpcError({
        cause: data.error instanceof Error ? data.error : undefined,
        code: 'json_rpc_error',
        shortMessage: `RPC Error: ${data.error.message}`,
      });
    }

    // The result is a hexadecimal string representing the balance in Wei
    const balanceWeiHex = data.result;

    // Convert the hexadecimal string to a BigInt
    const balanceWei = BigInt(balanceWeiHex);

    // Convert the balance from Wei to Ether (1 Ether = 10^18 Wei)
    // We use a BigInt for the conversion to maintain precision with large numbers
    const oneEther = BigInt(1e18);

    // Perform the division and return the result as a number
    // Note: This conversion can lose precision for very small amounts, but is generally fine for display purposes.
    const balanceEther = Number(balanceWei) / Number(oneEther);

    return balanceEther.toString();
  } catch (error) {
    if (error instanceof Error) {
      throw new RpcError({
        cause: error,
        code: 'rpc_error',
        shortMessage: `Failed to fetch balance for address ${address}`,
      });
    }

    throw error;
  }
};
